import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

import { ChooseProperty } from "./slices/ChooseProperty";
import { InviteApplicants as InviteApplicantsView } from "./slices/InviteApplicantsView";
import { ManualOption } from "./types";
import { SelfServeOption } from "./types";
import { toast } from "react-toastify";
import {
  ApplicantStatus,
  ApplicantType,
  Application,
  Money
} from "shared/types";
import { Template } from "types/landlord/verificationTemplate";
import { useNavigate } from "react-router-dom";
import api from "api/landlord/address";
import { RentAmountData } from "./components/SetRentAmountOptions";
import { useAuthContext } from "providers/Auth";
import { Lead } from "views/leads-id/types";
import { getDuplicateApplicantOnUnitError } from "utils/error";
export const InviteApplicants = ({
  setView,
  setOpen,
  unitId,
  propertyId,
  // NOTE: could skip the select unit/property step when unitId and propertyId are passed
  onInviteRefreshKey,
  applicationId,
  propertyName,
  unitName,
  unitSelectSkipped,
  disabledRoles,
  leadInfo,
  leadId,
  stayOnPage,
  onInvited
}: {
  setView: Dispatch<SetStateAction<"invite" | "manual_application">>;
  setOpen: (open: boolean) => void;
  onSuccess: (data: { applicationId: number; applicantId: number }) => void;
  unitId?: number;
  propertyId?: number;
  onInviteRefreshKey?: unknown[];
  applicationId?: number;
  propertyName?: string;
  unitName?: string;
  unitSelectSkipped: boolean;
  disabledRoles?: ApplicantType[];
  leadInfo?: Pick<Lead, "first_name" | "last_name" | "primary_email">;
  leadId?: number;
  stayOnPage?: boolean;
  onInvited?: () => void;
}) => {
  const { permissions, pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;
  const queryClient = useQueryClient();
  const [selectedUnitId, setSelectedUnitId] = useState<number | undefined>();
  const [selectedPropertyId, setSelectedPropertyId] = useState<
    number | undefined
  >();
  const [manualRent, setManualRent] = useState<RentAmountData | undefined>();
  const [useListedRent, setUseListedRent] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<
    Template | undefined
  >();
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<number | null>(
    null
  );
  const canOverrideApplicantScreeningTemplate = Boolean(
    permissions.canOverrideApplicantScreeningTemplate
  );

  useEffect(() => {
    if (propertyId) setSelectedPropertyId(propertyId);
  }, [propertyId]);

  useEffect(() => {
    if (unitId) setSelectedUnitId(unitId);
  }, [unitId]);

  const navigate = useNavigate();

  const [stepIdx, setStepIdx] = useState(
    propertyId && unitId && applicationId ? 1 : 0
  );

  const handleInviteApplicants = useMutation({
    mutationKey: ["invite-applicants"],
    onSuccess: (data: { application: Application }) => {
      if (onInviteRefreshKey && onInviteRefreshKey.length)
        queryClient.invalidateQueries({
          queryKey: onInviteRefreshKey
        });
      onInvited?.();

      const findManualInprogress = data.application.applicants.find(
        (applicant) =>
          applicant.is_manual &&
          applicant.application_status !== ApplicantStatus.Submitted
      );

      toast.success("Applicants invited successfully");

      if (findManualInprogress) setView("manual_application");

      if (data.application.id && (!stayOnPage || findManualInprogress))
        navigate(
          `/applications/${data.application.id}${findManualInprogress ? "?manualApplicantId=" + findManualInprogress.id : ""}`
        );

      setOpen(false);
    },
    onError: (error) => {
      const dup = getDuplicateApplicantOnUnitError(error);
      if (dup) return toast.error(dup.message);
      toast.error("Failed to invite applicants");
    },
    mutationFn: async (data: {
      applicants: {
        email?: string;
        phone?: string;
        unit_id: number;
        property_id: number;
        role: string;
        monthly_rent_amount: Money;
        first_name: string;
        middle_name?: string;
        last_name: string;
        lead_id?: number;
      }[];
      application_id?: number;
      unit_id: number;
      property_id: number;
      manual_rent?: RentAmountData;
      verification_template_id?: string;
      assignee_ids?: number[];
    }) => {
      let unit_monthly_rent_amount: Money | undefined;
      if (useListedRent) {
        const unit = await api.getUnitByPropertyAndUnitId(
          String(data.property_id),
          String(data.unit_id)
        );
        unit_monthly_rent_amount = unit.monthly_rent_amount;
      }

      return instance
        .post<{
          application: Application;
        }>("/portal/v1/applications/bulk_create", {
          ...data,
          applicants: data.applicants.map((applicant) => ({
            ...applicant,
            monthly_rent_amount:
              unit_monthly_rent_amount || applicant.monthly_rent_amount
          }))
        })
        .then(({ data }) => data);
    }
  });

  const monthlyRentAmount: Money = useMemo(() => {
    if (!useListedRent)
      if (homeSiteRentEnabled)
        return {
          cents:
            (manualRent?.site_rent_amount?.cents || 0) +
            (manualRent?.home_rent_amount?.cents || 0),
          currency: "USD"
        };
      else
        return { cents: manualRent?.rent_amount?.cents || 0, currency: "USD" };

    return {
      cents: 0,
      currency: "USD"
    };
  }, [useListedRent, homeSiteRentEnabled, manualRent]);

  if (stepIdx === 0)
    return (
      <ChooseProperty
        setStepIdx={setStepIdx}
        setOpen={setOpen}
        setSelectedPropertyId={setSelectedPropertyId}
        setSelectedUnitId={setSelectedUnitId}
        selectedPropertyId={selectedPropertyId}
        selectedUnitId={selectedUnitId}
        propertyName={propertyName}
        unitName={unitName}
        applicationId={applicationId}
        manualRent={manualRent}
        setManualRent={setManualRent}
        useListedRent={useListedRent}
        setUseListedRent={setUseListedRent}
        onTemplateChange={setSelectedTemplate}
        selectedAssigneeId={selectedAssigneeId}
        setSelectedAssigneeId={setSelectedAssigneeId}
      />
    );

  return (
    <InviteApplicantsView
      applicationId={applicationId}
      disabledRoles={disabledRoles}
      propertyId={propertyId || selectedPropertyId}
      unitSelectSkipped={unitSelectSkipped}
      setOpen={setOpen}
      unitId={selectedUnitId}
      selectedTemplate={selectedTemplate}
      isInviteLoading={handleInviteApplicants.isPending}
      setStepIdx={setStepIdx}
      leadInfo={leadInfo}
      handleInvite={(options: (SelfServeOption | ManualOption)[]) => {
        if (!selectedUnitId || !selectedPropertyId) return;

        handleInviteApplicants.mutate({
          applicants: options.map((option) => ({
            email: option.type === "self-serve" ? option.email : undefined,
            first_name: option.first_name || "",
            last_name: option.last_name || "",
            unit_id: selectedUnitId,
            property_id: selectedPropertyId,
            role: option.role.toString(),
            monthly_rent_amount: monthlyRentAmount,
            // Only the invitee matching the lead's email, so co-applicants don't reuse it.
            lead_id:
              leadId &&
              option.type === "self-serve" &&
              option.email &&
              leadInfo?.primary_email &&
              option.email.trim().toLowerCase() ===
                leadInfo.primary_email.trim().toLowerCase()
                ? leadId
                : undefined
          })),
          application_id: applicationId,
          unit_id: selectedUnitId,
          property_id: selectedPropertyId,
          manual_rent: !applicationId ? manualRent : undefined,
          verification_template_id: canOverrideApplicantScreeningTemplate
            ? selectedTemplate?.id
            : undefined,
          assignee_ids: selectedAssigneeId !== null ? [selectedAssigneeId] : []
        });
      }}
    />
  );
};
